import {
  TRestrictionsGroupMetadataFile,
  TRestrictionsGroupMetadataValue,
  TRestrictionsGroupType,
  IRestrictionsGroupMetadata,
} from '@/store/models/restriction.model';
import { ICursorElements } from '@/store/types/search.types';
import { createHttpHeader } from '@/utils/network/createHTTPHeaders';
import { decryptTreezorResponse } from '@/services/encryption/decryptTreezorResponse';
import { getStaticDashboardConfig } from '@/hooks/useDashboardConfig';
import { fetchQuery } from '@/utils/queryClient';
import { fetchMethods } from '@/types/FetchService.types';

export class RestrictionsGroupMetadataService {
  static queryKey = 'Restrictions Group Metadata';
  static getRestrictionsGroupMetadata = async (
    groupId: string | number,
    currentCursor?: string | null,
  ): Promise<ICursorElements<IRestrictionsGroupMetadata>> => {
    try {
      const config = getStaticDashboardConfig();

      const url = new URL(`${config.api.baseUrl}/core-connect/merchantIdRestrictionGroups/${groupId}/mid/metadata`);
      url.searchParams.append('pageSize', '50');

      if (currentCursor) url.searchParams.append('cursor', currentCursor);

      const restrictionsGroupMetadataResponse = await fetchQuery(
        [this.queryKey, { method: fetchMethods.GET, id: groupId }],
        url.toString(),
        {
          headers: createHttpHeader(),
        },
      );

      return await decryptTreezorResponse<IRestrictionsGroupMetadata>(restrictionsGroupMetadataResponse, {
        shouldSkipEncryption: true,
        hasCursor: true,
      });
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static updateRestrictionsGroupMetadataById = async (
    restrictionsGroupType: TRestrictionsGroupType,
    restrictionId: number | string,
    metadataValue: TRestrictionsGroupMetadataValue,
  ): Promise<IRestrictionsGroupMetadata> => {
    try {
      const config = getStaticDashboardConfig();

      const restrictionsGroupMetadataResponse = await fetchQuery(
        [
          this.queryKey,
          { method: fetchMethods.PUT, id: restrictionId, params: { restrictionsGroupType, metadataValue } },
        ],
        `${config.api.baseUrl}/core-connect/${restrictionsGroupType.toLowerCase()}/${restrictionId}/metadata`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify(metadataValue),
        },
      );

      const restrictionsGroupMetadata = await decryptTreezorResponse<IRestrictionsGroupMetadata>(
        restrictionsGroupMetadataResponse,
        { shouldSkipEncryption: true, isSingle: true },
      );

      restrictionsGroupMetadata.mid = restrictionId;
      return restrictionsGroupMetadata;
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static updateRestrictionsGroupMetadata = async (
    restrictionsGroupType: TRestrictionsGroupType,
    metadata: TRestrictionsGroupMetadataFile,
  ): Promise<IRestrictionsGroupMetadata[]> => {
    try {
      const restrictionsGroupMetadatas = await Promise.all(
        Object.keys(metadata).map((key) =>
          RestrictionsGroupMetadataService.updateRestrictionsGroupMetadataById(
            restrictionsGroupType,
            key,
            metadata[key],
          ),
        ),
      );

      return restrictionsGroupMetadatas;
    } catch (err) {
      return Promise.reject(err);
    }
  };

  static updateAllRestrictionsGroupMetadata = async (
    restrictionsGroupType: TRestrictionsGroupType,
    metadatas: IRestrictionsGroupMetadata[],
  ): Promise<IRestrictionsGroupMetadata[]> => {
    try {
      const config = getStaticDashboardConfig();

      const restrictionsMetadataResponse = await fetchQuery(
        [this.queryKey, { method: fetchMethods.PUT, params: { restrictionsGroupType, metadatas } }],
        `${config.api.baseUrl}/core-connect/${restrictionsGroupType.toLowerCase()}/metadata`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify(metadatas),
        },
      );

      return await decryptTreezorResponse<IRestrictionsGroupMetadata>(restrictionsMetadataResponse, {
        shouldSkipEncryption: true,
      });
    } catch (err) {
      return Promise.reject(err);
    }
  };
}
